import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { usePageMeta } from '../hooks/usePageMeta';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { sendContactForm } from '../lib/emailjs';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { useIsKenya, getPhoneDisplay } from '../hooks/useIsKenya';
const SUBMIT_API = 'https://contentful-bridge-75k0bmv20-belo-digitals-projects.vercel.app/api/submit';
export function ContactPage() {
  usePageMeta({
    title: 'Contact Us',
    description: "Get in touch with Belo Digital. Tell us about your project goals, challenges, or ideas — we'll help you find the right digital solution."
  });
  const isKenya = useIsKenya();
  const phoneDisplay = getPhoneDisplay(isKenya);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // 1. Send to Contentful
      const res = await fetch(SUBMIT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentType: 'contactSubmission',
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Submission failed');
      }
      // 2. Send via EmailJS
      await sendContactForm({
        name: formData.name,
        email: formData.email,
        message: formData.message
      });
      setSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <main className="pt-24" data-id="element-679">
      {/* Hero */}
      <Section background="default" data-id="element-680">
        <div className="max-w-4xl" data-id="element-681">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-6 leading-tight" data-id="element-682">
            Let's Talk About Your Project
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed" data-id="element-683">
            Tell us about your goals, challenges, or ideas - we'll help you find
            the right digital solution.
          </motion.p>
        </div>
      </Section>

      {/* Contact Form & Info */}
      <Section background="white" data-id="element-684">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-685">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-686">
            {submitted ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-col items-start justify-center h-full py-16" data-id="element-687">
                <div className="w-14 h-14 bg-[#00316B]/10 rounded-full flex items-center justify-center mb-6" data-id="element-688">
                  <Send className="w-6 h-6 text-[#00316B]" data-id="element-689" />
                </div>
                <h3 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-690">
                  Message Sent!
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-691">
                  Thank you for reaching out. We've received your message and
                  will get back to you within 2–4 business hours.
                </p>
                <button onClick={() => setSubmitted(false)} className="text-sm text-[#00316B] font-medium underline underline-offset-4 hover:text-[#FF6600] transition-colors" data-id="element-692">
                  Send another message
                </button>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-693">
                <div data-id="element-694">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-695">
                    Your Name *
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="Your Cute Name Here" data-id="element-696" />
                </div>

                <div data-id="element-697">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-698">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors" placeholder="Your Super Email Here" data-id="element-699" />
                </div>

                <div data-id="element-700">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-701">
                    Tell us about your project *
                  </label>
                  <textarea id="message" name="message" required rows={6} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors resize-none" placeholder="Describe your project, goals, timeline, and any other relevant details..." data-id="element-702" />
                </div>

                <Button type="submit" size="lg" className="w-full" isLoading={isSubmitting} rightIcon={<Send className="w-4 h-4" data-id="element-704" />} data-id="element-703">
                  Send Message
                </Button>
              </form>}
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:pl-8" data-id="element-705">
            <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-8" data-id="element-706">
              Get in Touch
            </h2>

            <div className="space-y-8 mb-12" data-id="element-707">
              <div className="flex items-start" data-id="element-708">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-709">
                  <Mail className="w-5 h-5 text-[#00316B]" data-id="element-710" />
                </div>
                <div data-id="element-711">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-712">Email</h3>
                  <a href="mailto:biz@belo.co.ke" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-713">
                    biz@belo.co.ke
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-714">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-715">
                  <Phone className="w-5 h-5 text-[#00316B]" data-id="element-716" />
                </div>
                <div data-id="element-717">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-718">Phone</h3>
                  <a href="tel:+254701660306" className="text-gray-600 hover:text-[#00316B] transition-colors" data-id="element-719">
                    {phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-720">
                <div className="w-12 h-12 bg-[#F8F8F6] rounded-lg flex items-center justify-center mr-4 shrink-0" data-id="element-721">
                  <MapPin className="w-5 h-5 text-[#00316B]" data-id="element-722" />
                </div>
                <div data-id="element-723">
                  <h3 className="font-semibold text-[#0C0C0C] mb-1" data-id="element-724">
                    Location
                  </h3>
                  <p className="text-gray-600" data-id="element-725">Nairobi, Kenya</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#F8F8F6] rounded-xl" data-id="element-726">
              <h3 className="font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-727">
                Response Time
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-728">
                We typically respond within 2-4 hours during business days. For
                urgent inquiries, please give us a call or send a message on
                WhatsApp.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>;
}